import React, { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import {
  ITicketBoard,
  TicketReportSpace,
  TicketVariant,
  ticketReportSpace,
  ticketVariant,
} from '../../../../../interfaces/models/ticket.interfaces'
import {
  infiniteTicketQueries,
  ticketQueries,
  updateTicketVariant,
} from '../../../../../api/ticket'
import { DropdownSelect } from '../../../../../components/common/DropdownSelect/DropdownSelect'
import { FormControl } from '../../../../../components/common/FormControl'
import {
  Button,
  ButtonVariant,
} from '../../../../../components/common/Button/Button'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { SPACE_TRANSLATION_KEY } from '../../../../../helpers/ticketSpace'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'

export const updateTicketVariantSchema = yup
  .object({
    variant: yup
      .mixed<TicketVariant>()
      .oneOf(['question', 'report'])
      .required(),
    space: yup
      .mixed<TicketReportSpace>()
      .oneOf(ticketReportSpace as unknown as TicketReportSpace[])
      .when('variant', {
        is: 'report',
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
  })
  .required()

export const SetVariantOnTicket = ({
  society,
  ticket,
}: {
  society: ISocietiesModel
  ticket: ITicketBoard
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const queryClient = useQueryClient()
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, isValid },
  } = useForm<InferType<typeof updateTicketVariantSchema>>({
    resolver: yupResolver(updateTicketVariantSchema),
    defaultValues: {
      space: ticket.space,
      variant: ticket.variant,
    },
  })

  const selectedVariant = watch('variant')

  const { setToastNotification } = useToastNotifications()
  const { isPending, mutate } = useMutation({
    mutationFn: async (body: {
      variant: TicketVariant
      space?: TicketReportSpace
    }) => {
      const response = await updateTicketVariant(
        { societyId: society._id, ticketId: ticket.id },
        body
      )
      if (!response.data.data) {
        throw new Error()
      }
      return response.data.data
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
    onSuccess: async (data) => {
      reset({ variant: data.variant, space: data.space })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: infiniteTicketQueries.ticketsBoard({
            societyId: society._id,
          }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: ticketQueries.getTicketAsBoard(ticket.id, society._id)
            .queryKey,
        }),
      ])
    },
  })

  const spaceOptions = useMemo(
    () =>
      ticketReportSpace.map((s) => ({
        value: s,
        label: translate(SPACE_TRANSLATION_KEY[s]),
      })),
    [translate]
  )

  const variantOptions = useMemo(
    () =>
      ticketVariant.map((v) => ({
        value: v,
        label: translate(`ticketBoardSidebar.variant.option.${v}`),
      })),
    [translate]
  )

  const submit = (data: InferType<typeof updateTicketVariantSchema>): void => {
    mutate({ variant: data.variant, space: data.space })
  }

  return (
    <div className="mb-4 flex flex-col gap-2">
      <FormControl
        label={translate('ticketBoardSidebar.variant.label')}
        name="SelectTicketVariant"
      >
        <Controller
          control={control}
          name="variant"
          render={({ field: { value, onChange } }) => (
            <DropdownSelect
              value={value}
              options={variantOptions}
              onChange={onChange}
              dropdownWidth="w-full"
            />
          )}
        />
      </FormControl>
      {selectedVariant === 'report' && (
        <FormControl
          label={translate('ticketView.form.labels.space')}
          name="SelectTicketSpace"
        >
          <Controller
            control={control}
            name="space"
            render={({ field: { value, onChange } }) => (
              <DropdownSelect
                value={value}
                options={spaceOptions}
                onChange={onChange}
                dropdownWidth="w-full"
              />
            )}
          />
        </FormControl>
      )}
      {isDirty && (
        <Button
          className="w-full"
          variant={ButtonVariant.PRIMARY}
          label={translate('ticketBoardSidebar.variant.save')}
          onClick={handleSubmit(submit)}
          disabled={isPending || !isValid}
        />
      )}
    </div>
  )
}
